import { BaccaratEvents } from '../events/baccarat.js';
import { getLogger } from '../logger.js';

const logger = getLogger('baccarat-validation');

/** Epsilon for cross-field money reconciliation (mirrors Python 1e-6). */
const RECONCILE_EPSILON = 1e-6;

/**
 * The client's placeholder for a card it will not show. Baccarat hands are
 * always face-up, so it is a contract violation rather than a lifecycle marker.
 */
const HIDDEN_CARD = 'cover';

/** The client's placeholder for an unknown playId — never a round id. */
const UNKNOWN_ROUND_ID = 'N/A';

/** Baccarat card values: ace 1, pips face value, tens and courts 0. */
const CARD_VALUES: Readonly<Record<string, number>> = {
  A: 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
  '9': 9,
  '10': 0,
  J: 0,
  Q: 0,
  K: 0,
};

const SUITS: ReadonlySet<string> = new Set(['H', 'D', 'C', 'S']);

/** The side each main bet backs — it wins exactly when the result names it. */
const BACKED_RESULT: Readonly<Record<string, BaccaratEvents.Result>> = {
  player: 'player',
  banker: 'banker',
  tie: 'tie',
};

/** Split a `"RANK-SUIT"` card string; throws on anything unparseable. */
function parseCard(card: string, where: string): { rank: string; suit: string } {
  if (card === HIDDEN_CARD) {
    throw new Error(`${where} "${HIDDEN_CARD}" is not a card: baccarat hands are face-up`);
  }
  if (typeof card !== 'string' || !card.includes('-')) {
    throw new Error(`${where} is unparseable: ${card}. Expected "RANK-SUIT"`);
  }
  const separator = card.indexOf('-');
  const rank = card.slice(0, separator);
  const suit = card.slice(separator + 1);
  if (!(rank in CARD_VALUES) || !SUITS.has(suit)) {
    throw new Error(`${where} is unparseable: ${card}. Unknown rank or suit`);
  }
  return { rank, suit };
}

/** Baccarat value of a hand: sum of card values, modulo 10. */
export function baccaratTotal(cards: string[]): number {
  return cards.reduce((acc, card) => acc + CARD_VALUES[parseCard(card, 'card').rank], 0) % 10;
}

/** The round result the two totals imply. */
export function compareHands(playerTotal: number, bankerTotal: number): BaccaratEvents.Result {
  if (playerTotal > bankerTotal) {
    return 'player';
  }
  if (bankerTotal > playerTotal) {
    return 'banker';
  }
  return 'tie';
}

/** Opening two cards share a rank. */
export function isPair(cards: string[]): boolean {
  if (cards.length < 2) {
    return false;
  }
  return parseCard(cards[0], 'card').rank === parseCard(cards[1], 'card').rank;
}

/** Opening two cards share a rank AND a suit (multi-deck shoe). */
export function isPerfectPair(cards: string[]): boolean {
  if (cards.length < 2) {
    return false;
  }
  const first = parseCard(cards[0], 'card');
  const second = parseCard(cards[1], 'card');
  return first.rank === second.rank && first.suit === second.suit;
}

/**
 * The complete key set of each nested model. Mirrors Pydantic's
 * `extra="forbid"`, which reaches inside the nested models too: an unknown key
 * is a contract mismatch, and neither side repairs or drops it — both reject.
 * A field the contract does not name is either something the cards already
 * decide (a carried `natural` could disagree with the hand) or a leftover the
 * agent would silently ignore.
 */
const BET_KEYS: readonly string[] = ['type', 'amount'];
const BET_OUTCOME_KEYS: readonly string[] = ['type', 'amount', 'outcome', 'payout'];
const HAND_KEYS: readonly string[] = ['cards', 'total'];

function rejectUnknownKeys(value: object, allowed: readonly string[], where: string): void {
  for (const key of Object.keys(value)) {
    if (!allowed.includes(key)) {
      throw new Error(`${where} has an unknown field: ${key}. Allowed: ${allowed.join(', ')}`);
    }
  }
}

/** Per-bet placement checks shared by all bet-carrying events. */
function validateBetPlacement(
  bet: BaccaratEvents.Bet,
  idx: number,
  allowed: readonly string[] = BET_KEYS
): void {
  if (!bet || typeof bet !== 'object') {
    throw new Error(`bets[${idx}] must be an object`);
  }
  rejectUnknownKeys(bet, allowed, `bets[${idx}]`);
  if (!BaccaratEvents.BET_TYPES.includes(bet.type)) {
    throw new Error(
      `bets[${idx}].type is invalid: ${bet.type}. Must be one of: ${BaccaratEvents.BET_TYPES.join(', ')}`
    );
  }
  if (typeof bet.amount !== 'number' || bet.amount <= 0 || !isFinite(bet.amount)) {
    throw new Error(`bets[${idx}].amount must be a positive number`);
  }
}

/** One entry per bet type — a repeat means a diff, not a full snapshot. */
function rejectDuplicateBetTypes(bets: BaccaratEvents.Bet[]): void {
  const seen = new Set<string>();
  for (const bet of bets) {
    if (seen.has(bet.type)) {
      throw new Error(`duplicate bet type: ${bet.type}`);
    }
    seen.add(bet.type);
  }
}

function sumAmount(bets: BaccaratEvents.Bet[]): number {
  return bets.reduce((acc, bet) => acc + bet.amount, 0);
}

function validateRoundId(roundId: string): void {
  if (typeof roundId !== 'string' || roundId.trim().length === 0) {
    throw new Error('roundId must be a non-empty string');
  }
  if (roundId.trim() === UNKNOWN_ROUND_ID) {
    throw new Error(`roundId "${UNKNOWN_ROUND_ID}" is a placeholder, not a round`);
  }
}

/** 2-3 face-up cards whose declared total is the baccarat value of the cards. */
function validateHand(hand: BaccaratEvents.Hand, side: string): void {
  if (!hand || typeof hand !== 'object') {
    throw new Error(`${side} must be an object`);
  }
  rejectUnknownKeys(hand, HAND_KEYS, side);
  if (!Array.isArray(hand.cards) || hand.cards.length < 2 || hand.cards.length > 3) {
    throw new Error(`${side}.cards must hold 2 or 3 cards`);
  }
  hand.cards.forEach((card, idx) => parseCard(card, `${side}.cards[${idx}]`));
  if (
    typeof hand.total !== 'number' ||
    !Number.isInteger(hand.total) ||
    hand.total < 0 ||
    hand.total > 9
  ) {
    throw new Error(`${side}.total must be an integer 0-9, got ${hand.total}`);
  }
  const derived = baccaratTotal(hand.cards);
  if (hand.total !== derived) {
    throw new Error(
      `${side}.total=${hand.total} != derived ${derived} for ${hand.cards.join(' ')}`
    );
  }
}

/**
 * `bets_update` variant: full live-table snapshot, so an empty array is a
 * valid state (cleared table) and totalBet may be 0. There is no roundId
 * during the betting window.
 */
export function validateBaccaratBetsUpdate(bets: BaccaratEvents.Bet[], totalBet: number): void {
  if (!Array.isArray(bets)) {
    throw new Error('bets must be an array');
  }
  for (const [idx, bet] of bets.entries()) {
    validateBetPlacement(bet, idx);
  }
  rejectDuplicateBetTypes(bets);
  if (typeof totalBet !== 'number' || totalBet < 0 || !isFinite(totalBet)) {
    throw new Error(`Invalid totalBet: ${totalBet}. Must be a non-negative number`);
  }
  const staked = sumAmount(bets);
  if (Math.abs(staked - totalBet) > RECONCILE_EPSILON) {
    throw new Error(`sum(bets[].amount)=${staked} must equal totalBet=${totalBet}`);
  }
}

export function validateBaccaratBetConfirmation(
  bets: BaccaratEvents.Bet[],
  totalBet: number,
  roundId: string
): void {
  if (!Array.isArray(bets) || bets.length === 0) {
    throw new Error('bets must be a non-empty array');
  }
  for (const [idx, bet] of bets.entries()) {
    validateBetPlacement(bet, idx);
  }
  rejectDuplicateBetTypes(bets);
  if (typeof totalBet !== 'number' || totalBet <= 0 || !isFinite(totalBet)) {
    throw new Error(`Invalid totalBet: ${totalBet}. Must be a positive number`);
  }
  const staked = sumAmount(bets);
  if (Math.abs(staked - totalBet) > RECONCILE_EPSILON) {
    throw new Error(`sum(bets[].amount)=${staked} must equal totalBet=${totalBet}`);
  }
  validateRoundId(roundId);
}

export interface BaccaratGameOverInput {
  playerHand: BaccaratEvents.Hand;
  bankerHand: BaccaratEvents.Hand;
  result: BaccaratEvents.Result;
  bets: BaccaratEvents.BetOutcome[];
  totalBet: number;
  totalWin: number;
  roundId: string;
}

/** Pair bets are decided by the opening two cards, not by the mapper. */
function pairHit(bet: BaccaratEvents.BetOutcome, player: string[], banker: string[]): boolean {
  switch (bet.type) {
    case 'player_pair':
      return isPair(player);
    case 'banker_pair':
      return isPair(banker);
    case 'either_pair':
      return isPair(player) || isPair(banker);
    default:
      return isPerfectPair(player) || isPerfectPair(banker);
  }
}

function validateSettledBet(
  bet: BaccaratEvents.BetOutcome,
  idx: number,
  input: BaccaratGameOverInput
): void {
  const where = `bets[${idx}]`;
  validateBetPlacement(bet, idx, BET_OUTCOME_KEYS);
  if (!BaccaratEvents.OUTCOMES.includes(bet.outcome)) {
    throw new Error(
      `${where}.outcome is invalid: ${bet.outcome}. Must be one of: ${BaccaratEvents.OUTCOMES.join(', ')}`
    );
  }
  // GROSS return: stake + profit, never negative; 0 if lost.
  if (typeof bet.payout !== 'number' || bet.payout < 0 || !isFinite(bet.payout)) {
    throw new Error(`${where}.payout must be a non-negative number`);
  }
  if (bet.outcome === 'won' && bet.payout <= 0) {
    throw new Error(`${where} won but payout=${bet.payout}`);
  }
  if (bet.outcome === 'lost' && bet.payout !== 0) {
    throw new Error(`${where} lost but payout=${bet.payout}`);
  }

  const isMain = BaccaratEvents.MAIN_BET_TYPES.includes(bet.type);
  // A push is a returned stake, and only a tie returns one — and only to the
  // two main sides.
  if (bet.outcome === 'push') {
    if (!isMain || input.result !== 'tie') {
      throw new Error(
        `${where} push is only a ${BaccaratEvents.MAIN_BET_TYPES.join('/')} stake on a tie ` +
          `(type=${bet.type}, result=${input.result})`
      );
    }
    if (Math.abs(bet.payout - bet.amount) > RECONCILE_EPSILON) {
      throw new Error(
        `${where} push returns the stake: payout=${bet.payout} != amount=${bet.amount}`
      );
    }
  } else if (isMain && input.result === 'tie') {
    throw new Error(`${where} ${bet.type} stake is pushed on a tie, not ${bet.outcome}`);
  }

  const backed = BACKED_RESULT[bet.type];
  if (backed) {
    if ((bet.outcome === 'won') !== (input.result === backed)) {
      throw new Error(
        `${where} ${bet.type} outcome=${bet.outcome} contradicts result=${input.result}`
      );
    }
    return;
  }
  if ((bet.outcome === 'won') !== pairHit(bet, input.playerHand.cards, input.bankerHand.cards)) {
    throw new Error(
      `${where} ${bet.type} outcome=${bet.outcome} contradicts the cards ` +
        `(player ${input.playerHand.cards.join(' ')}, banker ${input.bankerHand.cards.join(' ')})`
    );
  }
}

/**
 * Structural contradictions hard-reject — every one of them is derivable from
 * the cards, so a mismatch is a mapper bug. Money totals drift is logged and
 * accepted: `game_over` is the round's only speaking event and benign engine
 * drift must never drop it. Same split as the Pydantic model.
 */
export function validateBaccaratGameOver(input: BaccaratGameOverInput): void {
  const { playerHand, bankerHand, result, bets, totalBet, totalWin, roundId } = input;
  validateHand(playerHand, 'playerHand');
  validateHand(bankerHand, 'bankerHand');
  if (!BaccaratEvents.RESULTS.includes(result)) {
    throw new Error(
      `Invalid result: ${result}. Must be one of: ${BaccaratEvents.RESULTS.join(', ')}`
    );
  }
  const derived = compareHands(playerHand.total, bankerHand.total);
  if (result !== derived) {
    throw new Error(
      `result=${result} != derived ${derived} for player ${playerHand.total} vs banker ${bankerHand.total}`
    );
  }

  if (!Array.isArray(bets) || bets.length === 0) {
    throw new Error('bets must be a non-empty array');
  }
  rejectDuplicateBetTypes(bets);
  bets.forEach((bet, idx) => validateSettledBet(bet, idx, input));

  if (typeof totalBet !== 'number' || totalBet <= 0 || !isFinite(totalBet)) {
    throw new Error(`Invalid totalBet: ${totalBet}. Must be a positive number`);
  }
  if (typeof totalWin !== 'number' || totalWin < 0 || !isFinite(totalWin)) {
    throw new Error(`Invalid totalWin: ${totalWin}. Must be a non-negative number`);
  }
  validateRoundId(roundId);

  const staked = sumAmount(bets);
  if (Math.abs(staked - totalBet) > RECONCILE_EPSILON) {
    logger.warn(`game_over totals drift: sum(amount)=${staked} totalBet=${totalBet}`);
  }
  const returned = bets.reduce((acc, bet) => acc + bet.payout, 0);
  if (Math.abs(returned - totalWin) > RECONCILE_EPSILON) {
    logger.warn(`game_over totals drift: sum(payout)=${returned} totalWin=${totalWin}`);
  }
}
